import {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import styled from "@emotion/styled";
import {
  Modal,
  TextField,
  Typography,
  tokens,
} from "@sunrun/experience-ui-components";
import { MFE } from "@sunrun/sales-experience-shared";
import {
  MFE_KEYS,
  getDefaultStagingOrigin,
  readMfeOverrides,
  reloadWindow,
  writeMfeOverrides,
  type MfeOverrides,
} from "helpers/mfeOverrides";

const HIDDEN_OVERRIDE_INPUTS = new Set<MFE>(["leagueLive", "splat"]);

type DevMenuContextValue = {
  isEnabled: boolean;
  hasOverrides: boolean;
  openMenu: () => void;
  clearOverrides: () => void;
};

const DevMenuContext = createContext<DevMenuContextValue>({
  isEnabled: false,
  hasOverrides: false,
  openMenu: () => undefined,
  clearOverrides: () => undefined,
});

const emptyFormValues = (): Record<MFE, string> =>
  Object.fromEntries(MFE_KEYS.map((mfe) => [mfe, ""])) as Record<MFE, string>;

const formValuesFromStorage = (): Record<MFE, string> => {
  const stored = readMfeOverrides();
  const values = emptyFormValues();
  for (const mfe of MFE_KEYS) {
    values[mfe] = HIDDEN_OVERRIDE_INPUTS.has(mfe) ? "" : stored[mfe] ?? "";
  }
  return values;
};

const useDevMenu = (): DevMenuContextValue => useContext(DevMenuContext);

const DevMenuProvider = ({
  children,
}: {
  children: ReactNode;
}): ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const [formValues, setFormValues] =
    useState<Record<MFE, string>>(emptyFormValues);

  const openMenu = useCallback(() => {
    setFormValues(formValuesFromStorage());
    setIsOpen(true);
  }, []);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleChange = useCallback((mfe: MFE, value: string) => {
    setFormValues((current) => ({ ...current, [mfe]: value }));
  }, []);

  const handleClearAll = useCallback(() => {
    setFormValues(emptyFormValues());
  }, []);

  const handleSave = useCallback(() => {
    const overrides: MfeOverrides = {};
    for (const mfe of MFE_KEYS) {
      overrides[mfe] = HIDDEN_OVERRIDE_INPUTS.has(mfe) ? "" : formValues[mfe];
    }
    writeMfeOverrides(overrides);
    reloadWindow();
  }, [formValues]);

  const clearOverrides = useCallback(() => {
    writeMfeOverrides({});
    reloadWindow();
  }, []);

  const hasOverrides = Object.keys(readMfeOverrides()).length > 0;

  const contextValue = useMemo(
    () => ({
      isEnabled: true,
      hasOverrides,
      openMenu,
      clearOverrides,
    }),
    [hasOverrides, openMenu, clearOverrides]
  );

  return (
    <DevMenuContext.Provider value={contextValue}>
      {children}
      {isOpen && (
        <DevMenuModalShell onMouseDown={(e) => e.stopPropagation()}>
          <Modal
            testId="dev-menu-modal"
            type="info"
            hideIcon
            customModalWidth={720}
            title="MFE overrides"
            onClose={closeMenu}
            primaryButton={{
              text: "Save and reload",
              onClick: handleSave,
              testId: "dev-menu-save",
            }}
            secondaryButton={{
              text: "Cancel",
              onClick: closeMenu,
              testId: "dev-menu-cancel",
            }}
          >
            <FormHeader>
              <FormCopy>
                <Typography
                  size={14}
                  color={tokens.TINTS_OFF_WHITE_20}
                  lineHeight={20}
                >
                  Override iframe origins for this browser tab. Leave a field
                  blank to use the default staging URL. Query params still win
                  when both are set.
                </Typography>
                <Typography
                  size={14}
                  color={tokens.TINTS_OFF_WHITE_20}
                  lineHeight={20}
                >
                  * Include http:// or https:// in every URL. A hostname without
                  a protocol will not load.
                </Typography>
              </FormCopy>
              <ClearButton
                type="button"
                onClick={handleClearAll}
                data-testid="dev-menu-clear-all"
              >
                Clear all
              </ClearButton>
            </FormHeader>
            <FormBody>
              {MFE_KEYS.map((mfe) => {
                if (HIDDEN_OVERRIDE_INPUTS.has(mfe)) {
                  return null;
                }
                if (mfe === "offer") {
                  return (
                    <FieldNote
                      key={mfe}
                      data-testid="dev-menu-offer-unavailable"
                    >
                      <FieldNoteLabel>offer</FieldNoteLabel>
                      <Typography
                        size={14}
                        color={tokens.TINTS_OFF_WHITE_20}
                        lineHeight={20}
                      >
                        Offer experience cannot be overridden from this menu.
                      </Typography>
                    </FieldNote>
                  );
                }
                return (
                  <TextField
                    key={mfe}
                    label={mfe}
                    name={mfe}
                    value={formValues[mfe]}
                    placeholder={getDefaultStagingOrigin(mfe)}
                    onChange={(event) => handleChange(mfe, event.target.value)}
                    data-testid={`dev-menu-input-${mfe}`}
                  />
                );
              })}
            </FormBody>
          </Modal>
        </DevMenuModalShell>
      )}
    </DevMenuContext.Provider>
  );
};

const DevNavButton = (): ReactElement | null => {
  const { isEnabled, hasOverrides, openMenu, clearOverrides } = useDevMenu();
  if (!isEnabled) {
    return null;
  }

  return (
    <NavTopBar>
      <NavTopDevButton
        type="button"
        onClick={openMenu}
        data-testid="dev-menu-button"
        aria-label="Open MFE overrides"
      >
        Dev
      </NavTopDevButton>
      {hasOverrides && (
        <NavTopClearButton
          type="button"
          onClick={clearOverrides}
          data-testid="dev-menu-clear-overrides"
          aria-label="Clear MFE overrides"
        >
          ×
        </NavTopClearButton>
      )}
    </NavTopBar>
  );
};

const DevMoreMenuItem = ({
  onOpened,
}: {
  onOpened?: () => void;
}): ReactElement | null => {
  const { isEnabled, openMenu } = useDevMenu();
  if (!isEnabled) {
    return null;
  }

  return (
    <>
      <MoreMenuDevSeparator />
      <MoreMenuDevSection>
        <MoreMenuDevButton
          type="button"
          onClick={() => {
            openMenu();
            onOpened?.();
          }}
          data-testid="mobile-menu-item-dev"
        >
          Dev
        </MoreMenuDevButton>
      </MoreMenuDevSection>
    </>
  );
};

const DEV_ACCENT = "#F5A623";
const DEV_ACCENT_TEXT = "#101426";

const NavTopBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
  display: flex;
  align-items: stretch;
  height: 22px;
  background: ${DEV_ACCENT};
  color: ${DEV_ACCENT_TEXT};
`;

const NavTopDevButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  font-family: ${tokens.FONT_FAMILIES_ROOBERT};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  line-height: 1;
  cursor: pointer;

  &:hover,
  &:focus {
    filter: brightness(1.08);
  }
`;

const NavTopClearButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  padding: 0;
  border: none;
  border-left: 1px solid rgba(16, 20, 38, 0.25);
  background: transparent;
  color: inherit;
  font-family: ${tokens.FONT_FAMILIES_ROOBERT};
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;

  &:hover,
  &:focus {
    background: rgba(16, 20, 38, 0.12);
  }
`;

const MoreMenuDevButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 16px 0;
  margin-bottom: 8px;
  border: none;
  background: transparent;
  color: ${DEV_ACCENT};
  font-family: ${tokens.FONT_FAMILIES_ROOBERT};
  font-size: 16px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  border-radius: 8px;

  &:hover,
  &:focus {
    background-color: #22263a;
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const MoreMenuDevSection = styled.div`
  padding: 0 24px;
`;

const MoreMenuDevSeparator = styled.div`
  height: 1px;
  background-color: #49517d;
  margin: 24px 24px;
`;

const FormBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: min(60vh, 520px);
  overflow-y: auto;
  padding: 4px 4px 8px 0;

  @media (max-width: 750px) {
    max-height: none;
    overflow: visible;
  }
`;

const FormCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const ClearButton = styled.button`
  flex-shrink: 0;
  margin-top: 2px;
  padding: 0;
  border: none;
  background: none;
  color: ${tokens.BRAND_HERO_BLUE};
  font-family: ${tokens.FONT_FAMILIES_ROOBERT};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;

  &:hover,
  &:focus {
    filter: brightness(1.1);
  }
`;

const FieldNote = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldNoteLabel = styled.span`
  font-family: ${tokens.FONT_FAMILIES_ROOBERT};
  font-size: 14px;
  font-weight: 600;
  color: ${tokens.TINTS_OFF_WHITE_20};
`;

const DevMenuModalShell = styled.div`
  @media (max-width: 750px) {
    #modal-container {
      top: 0;
      left: 0;
      transform: none;
      width: 100% !important;
      height: 100vh;
      height: 100dvh;
      max-height: 100vh;
      max-height: 100dvh;
      border-radius: 0;
      border: none;
    }

    #modal-content-container {
      max-height: none !important;
      flex: 1 1 auto;
      overflow-y: auto;
    }
  }
`;

export { DevMenuProvider, DevMoreMenuItem, DevNavButton };
